import { default as React, FC, useEffect, useRef, useState } from 'react';

import { Col, Row } from 'antd';
import { useIntl } from 'react-intl';
import * as api from 'services/api';
import apiPaths from 'services/apiPaths';
import CustomModal from 'src/components/CustomModal';
import { CheckoutStatusEnum, CheckoutTypeEnum } from 'src/shared/enums';
import { IChallengeCheckoutPayment } from 'src/shared/models';
import { customFormatMessage, getRegionName, getRegions } from 'utils';

interface OwnProps {
  config: IChallengeCheckoutPayment;
  setShowCompletedModal: React.Dispatch<React.SetStateAction<boolean>>;
  showCompletedModal: boolean;
}

const CompletedCheckoutPayment: FC<OwnProps> = ({
  config,
  setShowCompletedModal,
  showCompletedModal,
}) => {
  const {
    product,
    checkoutId,
    checkoutName,
    completedLabel,
    qrUrl,
    checkoutDoneMessage,
  } = config;
  const [checkout, setCheckout] = useState(null);
  const [regionList, setRegionList] = useState([]);
  const { formatMessage } = useIntl();
  const pharmacyRef = useRef(null);

  const checkoutReady = checkout?.status === CheckoutStatusEnum.SAP_PROCESSED;
  const pharmacyCheckout = false;
  const singleProduct = true;
  const selectedProduct = product;

  useEffect(() => {
    async function getCheckoutStatus() {
      try {
        const response = await api.getDataByIdCall({
          dataPath: apiPaths.CHALLENGES.CHECKOUT_STATUS,
          id: checkoutId,
          callConfig: {},
        });
        if (!response?.data) return;

        setCheckout(response.data);
      } catch (err) {
        console.group(err);
      }
    }
    async function fetchRegions() {
      const regions = await getRegions();
      setRegionList(regions);
    }

    if (checkoutId) Promise.all([getCheckoutStatus(), fetchRegions()]);
  }, [checkoutId]);

  const checkoutCompletedModal = (
    <Row justify="center">
      <Col xs={24}>
        <p className="checkout__completed-modal-text">
          <strong>
            {formatMessage({
              id: 'page.experience.checkout.completed.quick-response.modal',
            })}
          </strong>
        </p>
        <img className="checkout__completed-modal-image" src={qrUrl} alt="QR" />
      </Col>
    </Row>
  );

  const completedCheckoutText = () => {
    const pickUpMessage =
      'page.experience.checkout.completed.selected-product.user-address-single';

    const pickUpStreet = `${checkout?.address}`.replace(/[#]+/g, ', ');
    const pickUpLocation = `${checkout?.zipCode} ${
      checkout?.city
    }, ${getRegionName(regionList, checkout?.regionName)}`;

    const showPickUpMessage = !singleProduct || !pharmacyCheckout;

    return (
      <>
        <p className="checkout__completed-text">
          {showPickUpMessage && (
            <span className="checkout__completed-text--product">
              {customFormatMessage(pickUpMessage, {
                checkoutName: product.lead,
                product: selectedProduct?.lead?.toLocaleLowerCase(),
              })}
            </span>
          )}
        </p>
        <ul ref={pharmacyRef} className="checkout__completed-pharmacy fs-mask">
          <li className="checkout__completed-pharmacy--street">
            {pickUpStreet}
          </li>
          <li className="checkout__completed-pharmacy--location">
            {pickUpLocation}
          </li>
        </ul>
      </>
    );
  };

  const getCheckoutSoonMessage = (
    <Col xs={24} className="checkout__completed-column">
      <p className="checkout__completed-column--qr-message">
        {checkoutDoneMessage ??
          formatMessage(
            {
              id: pharmacyCheckout
                ? 'page.experience.checkout.completed.soon'
                : 'page.experience.checkout.completed.soon.user-address',
            },
            { checkoutName: product.lead },
          )}
      </p>
    </Col>
  );

  const getCheckoutReadyMessage = () => {
    const showQrButton = checkoutReady && pharmacyCheckout;
    if (!showQrButton) return;

    return (
      <Col xs={24} className="checkout__completed-column">
        <p className="checkout__completed-column--qr-message">
          {formatMessage({
            id: 'page.experience.checkout.completed.quick-response',
          })}
        </p>
        <button
          className="checkout__completed-modal-button"
          onClick={() => setShowCompletedModal(true)}>
          {formatMessage({
            id: 'page.experience.checkout.completed.button',
          })}
        </button>
      </Col>
    );
  };

  return (
    <>
      <Row className="checkout__completed" justify="center">
        <Col xs={24} md={16} xl={12} className="checkout__completed-column">
          <Row justify="center">
            <Col xs={24} className="checkout__completed-column">
              <div className="checkout__completed-badge badge-success">
                {completedLabel}
              </div>
              {completedCheckoutText()}
            </Col>
          </Row>
          <Row justify="center">
            {checkoutReady ? getCheckoutReadyMessage() : getCheckoutSoonMessage}
          </Row>
          <Row>
            <CustomModal
              {...{
                visible: showCompletedModal,
                content: checkoutCompletedModal,
                onCancel: () => {
                  setShowCompletedModal(false);
                  pharmacyRef?.current.scrollIntoView(true);
                },
                footer: null,
                showIcon: false,
                customClass: 'checkout__completed-modal',
              }}
            />
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default CompletedCheckoutPayment;
